import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, 
  CheckCircle, 
  Shield, 
  Smartphone, 
  CreditCard,
  TrendingUp,
  Clock,
  Award,
  KeyRound,
  UserPlus,
  LogIn,
  Sparkles
} from 'lucide-react';
import { BrandMark } from '../shared/BrandMark';

type AuthVariant = 'default' | 'try-free' | 'login' | 'register' | 'reset';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  variant?: AuthVariant;
}

interface VariantStaticConfig {
  icon: React.ElementType;
  accentColor: string;
  bulletIcons: React.ElementType[];
  href?: string;
}

const variantStaticConfig: Record<AuthVariant, VariantStaticConfig> = {
  default: {
    icon: BookOpen,
    accentColor: 'text-primary',
    bulletIcons: [CheckCircle, TrendingUp, Shield],
  },
  'try-free': {
    icon: Sparkles,
    accentColor: 'text-secondary',
    bulletIcons: [BookOpen, CheckCircle, Clock],
    href: '/login',
  },
  register: {
    icon: UserPlus,
    accentColor: 'text-primary',
    bulletIcons: [CheckCircle, TrendingUp, Award],
    href: '/login',
  },
  login: {
    icon: LogIn,
    accentColor: 'text-primary',
    bulletIcons: [BookOpen, TrendingUp, Sparkles],
    href: '/register',
  },
  reset: {
    icon: KeyRound,
    accentColor: 'text-primary',
    bulletIcons: [Shield, CheckCircle, Clock],
    href: '/login',
  },
};

const trustBadgeIcons = [CreditCard, CheckCircle, Smartphone];

const variantToTranslationKey: Record<AuthVariant, string> = {
  default: 'auth.tryFree',
  'try-free': 'auth.tryFree',
  register: 'auth.register',
  login: 'auth.login',
  reset: 'auth.resetPassword.request',
};

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const config = variantStaticConfig[variant];
  const IconComponent = config.icon;
  const tKey = variantToTranslationKey[variant];
  const eyebrow = t(`${tKey}.eyebrow`);
  const bullets: string[] = t(`${tKey}.bullets`, { returnObjects: true }) as string[];
  const bottomLinkText = config.href ? t(`${tKey}.bottomLink.text`, { defaultValue: '' }) : '';
  const bottomLinkLabel = config.href ? t(`${tKey}.bottomLink.linkText`, { defaultValue: '' }) : '';
  const trustBadgeKeys = ['noCreditCard', 'cancelAnytime', 'allDevices'] as const;

  return (
    <div className="page-shell px-4 py-10 md:px-6 md:py-14">
      <div className="mx-auto w-full max-w-6xl">
        <div className="grid items-stretch gap-8 lg:grid-cols-2">
          <section className="order-2 rounded-[30px] border border-primary/10 bg-white/80 p-8 shadow-soft backdrop-blur-sm lg:order-1 lg:p-10">
            <Link to="/" className="mb-8 inline-flex">
              <BrandMark size="md" />
            </Link>

            <div className={`inline-flex items-center gap-2 ${config.accentColor} text-xs font-semibold uppercase tracking-[0.18em]`}>
              <IconComponent className="h-4 w-4" />
              <span>{eyebrow}</span>
            </div>

            <h1 className="mt-4 text-3xl font-bold text-ink lg:text-4xl">{title}</h1>

            {subtitle && (
              <p className="mt-4 max-w-xl text-base text-slate-600 lg:text-lg">{subtitle}</p>
            )}

            <ul className="mt-8 space-y-3">
              {Array.isArray(bullets) && bullets.map((text, index) => {
                const BulletIcon = config.bulletIcons[index] || CheckCircle;
                return (
                  <li key={index} className="flex items-start gap-3 rounded-2xl border border-primary/10 bg-white px-4 py-3">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <BulletIcon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm text-slate-700 lg:text-base">{text}</span>
                  </li>
                );
              })}
            </ul>

            {variant === 'try-free' && (
              <div className="mt-6 flex flex-wrap gap-3">
                {trustBadgeKeys.map((key, index) => {
                  const BadgeIcon = trustBadgeIcons[index];
                  return (
                    <span key={index} className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white px-3 py-1.5 text-xs text-slate-600">
                      <BadgeIcon className="h-3.5 w-3.5 text-primary" />
                      {t(`auth.tryFree.trustBadges.${key}`)}
                    </span>
                  );
                })}
              </div>
            )}

            {config.href && bottomLinkText && (
              <p className="mt-8 hidden text-sm text-slate-600 lg:block">
                {bottomLinkText}{' '}
                <Link to={config.href} className="font-semibold text-primary hover:underline">
                  {bottomLinkLabel}
                </Link>
              </p>
            )}
          </section>

          <section className="order-1 flex items-center lg:order-2">{children}</section>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
